import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { CONSENT_KEY, clearMarketingCookies, getConsent } from "@/lib/tracking";

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const open = () => {
      setMarketing(getConsent() === "accepted");
      setCustomize(false);
      setVisible(true);
    };
    window.addEventListener("cs-open-cookie-banner", open);
    let timer;
    if (!getConsent()) timer = setTimeout(() => setVisible(true), 1600);
    return () => {
      window.removeEventListener("cs-open-cookie-banner", open);
      clearTimeout(timer);
    };
  }, []);

  const choose = (value) => {
    const previous = getConsent();
    localStorage.setItem(CONSENT_KEY, value);
    window.dispatchEvent(new Event("cs-consent"));
    setVisible(false);
    if (previous === "accepted" && value === "rejected") {
      clearMarketingCookies();
      window.location.reload();
    }
  };

  const btnPrimary = "flex-1 bg-[#D4AF37] text-[#0A0A0C] text-[10px] tracking-[0.12em] uppercase font-semibold px-3 py-3 whitespace-nowrap hover:bg-[#F3E5AB] transition-colors duration-300";
  const btnGhost = "flex-1 border border-white/15 text-zinc-300 text-[10px] tracking-[0.12em] uppercase px-3 py-3 whitespace-nowrap hover:border-amber-500/50 hover:text-[#D4AF37] transition-colors duration-300";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[80] border border-amber-500/20 bg-[#121216]/95 backdrop-blur-xl p-5 sm:p-6"
          data-testid="cookie-banner"
        >
          <p className="text-sm text-zinc-300 leading-relaxed">
            {t("cookie_text")}{" "}
            <Link to="/privacy" data-testid="cookie-policy-link" className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F3E5AB] transition-colors duration-300">
              {t("cookie_link")}
            </Link>
          </p>

          {customize && (
            <div className="mt-5 space-y-3 border-t border-white/10 pt-4" data-testid="cookie-customize-panel">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-zinc-100 font-semibold">{t("cookie_necessary")}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{t("cookie_necessary_x")}</p>
                </div>
                <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 shrink-0 mt-1">{t("cookie_always")}</span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-zinc-100 font-semibold">{t("cookie_marketing")}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{t("cookie_marketing_x")}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={marketing}
                  data-testid="cookie-marketing-toggle"
                  onClick={() => setMarketing(!marketing)}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-300 ${marketing ? "bg-[#D4AF37]" : "bg-white/15"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#0A0A0C] transition-transform duration-300 ${marketing ? "translate-x-5" : ""}`} />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2.5 mt-5">
            {customize ? (
              <button data-testid="cookie-save-btn" onClick={() => choose(marketing ? "accepted" : "rejected")} className={btnPrimary}>
                {t("cookie_save")}
              </button>
            ) : (
              <>
                <button data-testid="cookie-accept-btn" onClick={() => choose("accepted")} className={btnPrimary}>
                  {t("cookie_accept")}
                </button>
                <button data-testid="cookie-reject-btn" onClick={() => choose("rejected")} className={btnGhost}>
                  {t("cookie_reject")}
                </button>
                <button data-testid="cookie-customize-btn" onClick={() => setCustomize(true)} className={btnGhost}>
                  {t("cookie_customize")}
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
